// Explain mode: hover any work-item element and read exactly what it is,
// where it comes from and what it does.
//
// Switched on with ?explain=1 on any URL, or the "Explain" chip in a page
// header; the choice is kept in localStorage so it survives navigation. When
// it is off nothing here renders anything extra -- `Tip` is a plain MUI
// Tooltip and `Explain` is a fragment -- so the production page is unchanged.
//
// The text itself lives in explainText.js, one entry per element, so the
// whole vocabulary can be reviewed (and lifted into training material) in one
// file rather than hunted through components.

import { useEffect, useState } from 'react';
import { Box, Chip, Tooltip } from '@mui/material';
import { noteExplain } from '../../services/workTelemetry';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { VERIFIED_MARK } from './explainText';

const KEY = 'nexus.explain';
const listeners = new Set();

const readFlag = () => {
    try {
        if (typeof window !== 'undefined') {
            const q = new URLSearchParams(window.location.search).get('explain');
            if (q === '1' || q === 'true') { window.localStorage.setItem(KEY, '1'); return true; }
            if (q === '0' || q === 'false') { window.localStorage.setItem(KEY, '0'); return false; }
            return window.localStorage.getItem(KEY) === '1';
        }
    } catch { /* storage blocked: explain simply stays off */ }
    return false;
};

let on = readFlag();

export const isExplainOn = () => on;

export function setExplainMode(next) {
    on = !!next;
    try { window.localStorage.setItem(KEY, on ? '1' : '0'); } catch { /* ignore */ }
    listeners.forEach((fn) => fn(on));
}

export function useExplainMode() {
    const [state, setState] = useState(on);
    useEffect(() => {
        listeners.add(setState);
        return () => listeners.delete(setState);
    }, []);
    return state;
}

// Tooltip content: the explanation, pre-wrapped so a multi-paragraph entry
// reads as paragraphs rather than one run-on line.
// An entry stamped in explainText.js's VERIFIED_LIVE opens with a check line,
// rendered green so a reader can tell tested behaviour from described behaviour.
const ExplainBody = ({ text }) => {
    const s = String(text);
    const verified = s.startsWith(VERIFIED_MARK);
    const cut = verified ? s.indexOf('\n\n') : -1;
    const head = verified ? (cut >= 0 ? s.slice(0, cut) : s) : null;
    const body = verified ? (cut >= 0 ? s.slice(cut + 2) : '') : s;
    return (
        <Box component="span" sx={{ display: 'block', whiteSpace: 'pre-wrap', fontSize: 12,
            lineHeight: 1.45, maxWidth: 420 }}>
            {head && (
                <Box component="span" data-testid="explain-verified" sx={{ display: 'block', mb: 0.75,
                    color: '#4ade80', fontWeight: 700 }}>
                    {head}
                </Box>
            )}
            {body}
        </Box>
    );
};

// Which explanation was read: its opening words stand in for a key.
const readExplain = (text) => () => noteExplain(String(text).replace(/\s+/g, ' ').trim().slice(0, 80));

const slotProps = {
    tooltip: { sx: { maxWidth: 460, bgcolor: '#0b1a3a', border: '1px solid #3987e5',
        p: 1.25, borderRadius: '6px' } },
    arrow: { sx: { color: '#0b1a3a', '&::before': { border: '1px solid #3987e5' } } },
};

/**
 * A tooltip that shows `title` normally and `explain` in explain mode.
 *
 * Drop-in for MUI Tooltip on the chips that already have one: the
 * production hover text is untouched, and the explanation replaces it (rather
 * than stacking a second tooltip) when the reader asks for it.
 */
export function Tip({ title, explain, children, ...rest }) {
    const mode = useExplainMode();
    if (mode && explain) {
        return (
            <Tooltip title={<ExplainBody text={explain} />} arrow enterDelay={150}
                slotProps={slotProps} onOpen={readExplain(explain)} {...rest}>
                {children}
            </Tooltip>
        );
    }
    if (title === undefined || title === null || title === '') return children;
    return <Tooltip title={title} {...rest}>{children}</Tooltip>;
}

/**
 * Marks an element that has no tooltip of its own.
 *
 * In explain mode it is wrapped in a span with a dashed outline (so the eye
 * can find what is annotated) and gets the tooltip; off, it renders the
 * children alone. `block` for card-level elements, `sx` to let the wrapper
 * take a flex slot (e.g. flex: 1 for a title).
 */
export function Explain({ text, children, block = false, sx = {} }) {
    const mode = useExplainMode();
    if (!mode || !text) return children;
    return (
        <Tooltip title={<ExplainBody text={text} />} arrow enterDelay={150} slotProps={slotProps}
            onOpen={readExplain(text)}>
            <Box component="span" sx={{
                display: block ? 'block' : 'inline-flex', alignItems: 'center',
                // A block wrapper must be as wide as what it wraps: Recharts'
                // ResponsiveContainer measures its parent, and a shrink-wrapped
                // span reads as width -1.
                ...(block ? { width: '100%' } : {}),
                outline: '1px dashed rgba(57,135,229,.7)', outlineOffset: 2, borderRadius: '4px',
                ...sx,
            }}>
                {children}
            </Box>
        </Tooltip>
    );
}

/** The header chip that turns explain mode on and off. */
export function ExplainToggle({ sx = {} }) {
    const mode = useExplainMode();
    return (
        <Tooltip title={mode
            ? 'Explain mode is on: hover any work element for exactly what it is and how it is computed. Click to turn off.'
            : 'Turn on explain mode: every work element gets a hover that says exactly what it is, where the value comes from and what clicking it does.'}>
            <Chip
                size="small" clickable onClick={() => setExplainMode(!mode)}
                icon={<HelpOutlineIcon sx={{ fontSize: 15 }} />}
                label={mode ? 'Explain: on' : 'Explain'}
                variant={mode ? 'filled' : 'outlined'}
                sx={{ fontSize: 11.5, fontWeight: 600, height: 24,
                    color: mode ? '#fff' : 'var(--color-text-secondary)',
                    bgcolor: mode ? '#1d4ed8' : 'transparent',
                    borderColor: mode ? '#1d4ed8' : 'var(--color-border, rgba(127,127,127,.4))',
                    '&:hover': { bgcolor: mode ? '#1e40af' : 'rgba(127,127,127,.1)' }, ...sx }}
            />
        </Tooltip>
    );
}
